package hk.com.vr360_demo2.webapp.component.viewer.component


import emotion.react.css
import js.coroutines.awaitCancellation
import hk.com.vr360_demo2.webapp.Colors
import hk.com.vr360_demo2.webapp.component.common.attachHorizontalDragScroll
import hk.com.vr360_demo2.webapp.component.marzipano.PanoramaSceneConfig
import hk.com.vr360_demo2.webapp.core.net.common.PropertyInfo
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.img
import react.dom.html.ReactHTML.span
import react.useLayoutEffect
import react.useRef
import react.useState
import web.cssom.AlignItems
import web.cssom.Border
import web.cssom.BoxSizing
import web.cssom.Cursor
import web.cssom.Display
import web.cssom.FlexDirection
import web.cssom.JustifyContent
import web.cssom.LineStyle
import web.cssom.NamedColor
import web.cssom.ObjectFit
import web.cssom.Padding
import web.cssom.Position
import web.cssom.WhiteSpace
import web.cssom.ZIndex
import web.cssom.pct
import web.cssom.px
import web.html.HTMLDivElement
import web.html.HTMLElement

external interface ViewerBottomInfoOverlayProps : Props {
    var propertyInfo: PropertyInfo?
    var uid: String?
    /** 接口 `PanoramaDataJsonResult.version`，`1` 為公司版 */
    var version: Int?
    /** 全景數據 `title`（與頂欄標題一致），例如 WhatsApp 預填「物業地址」 */
    var propertyName: String?
    var propertyLayout: String?
    var disclaimerText: String?
    var scenes: List<PanoramaSceneConfig>
    var currentSceneIndex: Int
    var onSwitchScene: (Int) -> Unit
}

private external interface ViewerPropertyInfoBarProps : Props {
    var propertyInfo: PropertyInfo?
    var uid: String?
    var version: Int?
    /** 全景接口 `PanoramaDataJsonResult.title` */
    var propertyTitle: String?
    var onSwitch: (() -> Unit)?
}

private external interface ViewerSceneSwitchPopupProps : Props {
    var visible: Boolean
    var scenes: List<PanoramaSceneConfig>
    var currentSceneIndex: Int
    var onSwitchScene: (Int) -> Unit
}

private val ViewerPropertyInfoBar = FC<ViewerPropertyInfoBarProps> { props ->
    val info = props.propertyInfo
    div {
        css {
            display = Display.flex
            flexDirection = FlexDirection.row
            alignItems = AlignItems.center
            justifyContent = JustifyContent.flexStart
            gap = 44.px
            backgroundColor = Colors.Background.Semitransparent
            borderRadius = 8.px
            padding = Padding(12.px, 12.px)
            color = NamedColor.white
            asDynamic()["@media (max-width: 600px)"] = js("{}").unsafeCast<react.CSSProperties>().apply {
                gap = 12.px
                width = 100.pct
                boxSizing = BoxSizing.borderBox
            }
        }

        div {
            css {
                display = Display.flex
                flexDirection = FlexDirection.row
                alignItems = AlignItems.center
                gap = 0.px
                asDynamic().flex = 1
                asDynamic().minWidth = "0"
            }
            AvatarView {
                photoUrl = info?.photoUrl?.replace("{size}", "original")
            }
            NameCodeView {
                name = info?.userName
                code = info?.licenseNo
                companyName = info?.companyName
                showCompanyName = props.uid != null
            }
        }

        div {
            css { asDynamic().flexShrink = 0 }
            PhoneButton {
                mobile = info?.mobile
                propertyTitle = props.propertyTitle
                version = props.version
                uid = props.uid
            }
        }
        div {
            css { asDynamic().flexShrink = 0 }
            SwitchSpaceButton {
                onSwitch = props.onSwitch
                label = "選擇場景"
            }
        }
        div {
            css { asDynamic().flexShrink = 0 }
            ShareButton {
                label = "分享"
            }
        }
    }
}

private val ViewerSceneSwitchPopup = FC<ViewerSceneSwitchPopupProps> { props ->
    val scrollRef = useRef<HTMLDivElement>(null)
    val dragDidMoveRef = useRef(false)
    var centered by useState(true)

    useLayoutEffect(dependencies = arrayOf(props.scenes)) {
        val el = scrollRef.current
        if (el != null) {
            centered = el.scrollWidth <= el.clientWidth + 1
        }
    }

    useLayoutEffect(dependencies = arrayOf(props.visible, props.scenes)) {
        if (!props.visible) return@useLayoutEffect
        val el = scrollRef.current ?: return@useLayoutEffect
        val detach = el.attachHorizontalDragScroll(dragDidMoveRef)
        awaitCancellation(detach)
    }

    if (!props.visible) return@FC Unit

    div {
        css {
            position = Position.absolute
            left = 0.px
            right = 0.px
            asDynamic().bottom = "calc(100% + 12px)"
            zIndex = 1000.asDynamic().unsafeCast<ZIndex>()
            backgroundColor = Colors.Background.Semitransparent
            asDynamic().boxShadow = "0 0 16px rgba(0,0,0,0.15)"
            padding = 10.px
        }

        div {
            ref = scrollRef
            css {
                display = Display.flex
                flexDirection = FlexDirection.row
                asDynamic().overflowX = "auto"
                asDynamic().overflowY = "hidden"
                asDynamic().scrollbarWidth = "none"
                asDynamic().msOverflowStyle = "none"
                asDynamic()["&::-webkit-scrollbar"] = js("{}").unsafeCast<react.CSSProperties>().apply {
                    asDynamic().display = "none"
                }
                gap = 24.px
                cursor = Cursor.grab
                asDynamic().userSelect = "none"
                asDynamic()["touchAction"] = "pan-x"
                justifyContent = if (centered) JustifyContent.center else JustifyContent.flexStart
                asDynamic().transition = "justify-content 0.2s"
                asDynamic()["@media (max-width: 600px)"] = js("{}").unsafeCast<react.CSSProperties>().apply {
                    justifyContent = JustifyContent.flexStart
                }
            }

            props.scenes.forEachIndexed { index, scene ->
                button {
                    key = Key("${scene.name ?: ""}-$index")
                    asDynamic().type = "button"
                    css {
                        display = Display.flex
                        flexDirection = FlexDirection.column
                        alignItems = AlignItems.center
                        asDynamic().border = "0"
                        asDynamic().background = "transparent"
                        cursor = Cursor.pointer
                        asDynamic().outline = "none"
                        padding = 0.px
                    }
                    onClick = {
                        if (dragDidMoveRef.current == true) {
                            dragDidMoveRef.current = false
                        } else {
                            props.onSwitchScene(index)
                        }
                    }
                    img {
                        src = scene.cover ?: ""
                        alt = scene.name ?: ""
                        asDynamic().draggable = false
                        css {
                            width = 64.px
                            height = 64.px
                            objectFit = ObjectFit.cover
                            border = Border(
                                2.px,
                                LineStyle.solid,
                                if (props.currentSceneIndex == index) Colors.Stroke.Primary else Colors.Stroke.White,
                            )
                        }
                        onError = {
                            val target = it.target as? HTMLElement
                            target?.setAttribute("src", "/assets/images/default-avatar.svg")
                        }
                    }
                    div {
                        css {
                            marginTop = 8.px
                            fontSize = 12.px
                            color = NamedColor.white
                        }
                        +(scene.name ?: "")
                    }
                }
            }
        }
    }
}

val ViewerBottomInfoOverlay = FC<ViewerBottomInfoOverlayProps> { props ->
    var showSceneSwitchPopup by useState(true)
    var showDisclaimer by useState(false)

    div {
        css {
            display = Display.flex
            flexDirection = FlexDirection.row
            justifyContent = JustifyContent.center
            alignItems = AlignItems.center
            position = Position.relative
            width = 100.pct
            paddingLeft = 16.px
            paddingRight = 16.px
            asDynamic().paddingBottom = "max(12px, calc(0px + env(safe-area-inset-bottom, 0px)))"
            asDynamic()["@media (max-width: 600px)"] = js("{}").unsafeCast<react.CSSProperties>().apply {
                asDynamic().paddingBottom = "max(0px, calc(0px + env(safe-area-inset-bottom, 0px)))"
            }
            asDynamic().pointerEvents = "auto"
        }

        ViewerSceneSwitchPopup {
            visible = showSceneSwitchPopup
            scenes = props.scenes
            currentSceneIndex = props.currentSceneIndex
            onSwitchScene = { props.onSwitchScene(it) }
        }

        div {
            css {
                position = Position.relative
                width = 100.pct
                asDynamic().flex = "1"
                asDynamic().minWidth = "0"
                display = Display.flex
                flexDirection = FlexDirection.row
                justifyContent = JustifyContent.center
                alignItems = AlignItems.center
                asDynamic()["@media (max-width: 600px)"] = js("{}").unsafeCast<react.CSSProperties>().apply {
                    flexDirection = FlexDirection.column
                    alignItems = AlignItems.stretch
                    asDynamic().gap = "8px"
                }
            }

            ViewerPropertyInfoBar {
                propertyInfo = props.propertyInfo
                uid = props.uid
                version = props.version
                propertyTitle = props.propertyName
                onSwitch = { showSceneSwitchPopup = !showSceneSwitchPopup }
            }

            button {
                title = "免責聲明"
                css {
                    asDynamic().display = "block"
                    position = Position.absolute
                    right = 0.px
                    top = 50.pct
                    asDynamic().transform = "translateY(-50%)"
                    zIndex = 1100.unsafeCast<ZIndex>()
                    background = "none".unsafeCast<web.cssom.Background>()
                    border = 0.px
                    cursor = Cursor.pointer
                    color = Colors.Text.White
                    padding = 0.px
                    whiteSpace = WhiteSpace.nowrap
                    asDynamic()["@media (max-width: 600px)"] = js("{}").unsafeCast<react.CSSProperties>().apply {
                        asDynamic().display = "block"
                        position = Position.relative
                        asDynamic().alignSelf = "flex-end"
                        asDynamic().top = "auto"
                        asDynamic().transform = "none"
                    }
                }
                onClick = { showDisclaimer = true }
                span {
                    css {
                        fontSize = 12.px
                        cursor = Cursor.pointer
                    }
                    +"免責聲明"
                }
            }
        }

        DisclaimerDialog {
            show = showDisclaimer
            onClose = { showDisclaimer = false }
        }
    }
}
